import { useCallback, useEffect, useMemo, useState } from 'react';
import type {
  HealthBand,
  NeedsAttentionCategory,
  NeedsAttentionSurface,
  NeedsAttentionSurfaceItem,
} from '@mvcp/contracts';
import { getNeedsAttention } from '../api';
// One owner for the band vocabulary and pill (Story 8.1). `BAND_ORDER` below stays
// local: it is sort weight, not vocabulary.
import { BAND_LABEL, BandPill } from './bands';
import type { DrillTarget } from './CockpitShell';

/** The category filter keys, in display order. `all` restores the full set. */
type CategoryFilter = NeedsAttentionCategory | 'all';
const CATEGORY_ORDER: CategoryFilter[] = ['all', 'expiring-soon', 'expired-missing', 'unverified'];
const CATEGORY_LABEL: Record<CategoryFilter, string> = {
  all: 'All',
  'expiring-soon': 'Expiring soon',
  'expired-missing': 'Expired / missing',
  unverified: 'Unverified',
};

/** The two sort modes: the server's worst-first order, or soonest-expiry-first. */
type SortMode = 'worst' | 'expiry';

/** Rows beyond this count are windowed until the user asks to see all (AD-11). */
const PAGE_SIZE = 50;

/** Sort key: parse an expiry to millis; a missing/unparseable date sorts last. */
function expiryMillis(iso: string | null): number {
  if (!iso) return Number.POSITIVE_INFINITY;
  const t = Date.parse(iso);
  return Number.isNaN(t) ? Number.POSITIVE_INFINITY : t;
}

/** Worst-first ordering weight for the surface (only `bad`/`warn` appear here). */
const BAND_ORDER: Record<HealthBand, number> = { bad: 0, warn: 1, neutral: 2, ok: 3 };

/** Short calendar date (`YYYY-MM-DD`) for a mono expiry cell; em-dash when absent. */
function shortDate(iso: string | null): string {
  if (!iso) return '—';
  const t = Date.parse(iso);
  return Number.isNaN(t) ? iso : new Date(t).toISOString().slice(0, 10);
}

type ViewState =
  | { kind: 'loading' }
  | { kind: 'ready'; surface: NeedsAttentionSurface }
  | { kind: 'error'; message: string };

/**
 * Compliance Manager cockpit → Needs Attention dedicated surface (Story 4.2, FR-19).
 * The full at-risk list behind the dashboard's 5-item preview: EVERY expiring /
 * expired-missing / unverified item, pre-sorted worst-status-first then soonest
 * expiry by the server (whose per-category counts equal the dashboard tiles by
 * construction). Presents count-badged category chips, a sort toggle, page-scoped
 * search, and 50-row pagination; every At-Risk/Action-Needed row shows a status pill
 * (dot + label) AND a one-line reason caption, and drills to its record on Open.
 * Launched in-pane by `CockpitShell` (mirroring the `frameworkDetailId` override).
 */
export function NeedsAttentionView({
  initialCategory,
  onBack,
  onOpenRecord,
}: {
  /** The category to preselect (a tile click), or `'all'` (the section "View all →"). */
  initialCategory: CategoryFilter;
  /** Close the surface (Back / Esc) — returns to the dashboard. */
  onBack: () => void;
  /** Drill to the item's subject (Vendor/Device) via the cockpit back stack. */
  onOpenRecord: (target: DrillTarget) => void;
}): React.JSX.Element {
  const [state, setState] = useState<ViewState>({ kind: 'loading' });

  const load = useCallback(async () => {
    setState({ kind: 'loading' });
    try {
      const surface = await getNeedsAttention();
      setState({ kind: 'ready', surface });
    } catch (err: unknown) {
      setState({ kind: 'error', message: err instanceof Error ? err.message : 'Failed to load' });
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Esc closes the surface (accessibility floor — mirrors the overlay contract).
  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') onBack();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onBack]);

  return (
    <>
      <button type="button" className="icon-button" onClick={onBack} style={{ alignSelf: 'flex-start' }}>
        ← Back
      </button>

      {state.kind === 'loading' && (
        <section className="card stack" aria-busy="true" aria-label="Loading needs attention">
          <p className="muted" style={{ margin: 0 }}>Loading…</p>
        </section>
      )}

      {state.kind === 'error' && (
        <section className="card stack" role="alert">
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>Couldn’t load needs attention</h2>
          <p className="muted" style={{ margin: 0 }}>{state.message}</p>
          <button
            type="button"
            className="btn-accent"
            data-surface="accent"
            onClick={() => void load()}
            style={{ alignSelf: 'flex-start' }}
          >
            Retry
          </button>
        </section>
      )}

      {state.kind === 'ready' && (
        <ReadySurface surface={state.surface} initialCategory={initialCategory} onOpenRecord={onOpenRecord} />
      )}
    </>
  );
}

function ReadySurface({
  surface,
  initialCategory,
  onOpenRecord,
}: {
  surface: NeedsAttentionSurface;
  initialCategory: CategoryFilter;
  onOpenRecord: (target: DrillTarget) => void;
}): React.JSX.Element {
  const [category, setCategory] = useState<CategoryFilter>(initialCategory);
  const [sort, setSort] = useState<SortMode>('worst');
  const [search, setSearch] = useState('');
  const [showAll, setShowAll] = useState(false);

  // A count per chip: `all` is the authoritative total; the three category counts
  // come straight from the server (identical-by-construction to the dashboard tiles).
  const chipCount: Record<CategoryFilter, number> = {
    all: surface.total,
    'expiring-soon': surface.counts.expiringSoon,
    'expired-missing': surface.counts.expiredMissing,
    unverified: surface.counts.unverifiedVendors,
  };

  const query = search.trim().toLowerCase();
  const filtered = useMemo(() => {
    const byCategory =
      category === 'all' ? surface.items : surface.items.filter((i) => i.category === category);
    const bySearch =
      query === ''
        ? byCategory
        : byCategory.filter(
            (i) =>
              i.label.toLowerCase().includes(query) ||
              (i.frameworkCode ?? '').toLowerCase().includes(query),
          );
    if (sort === 'worst') return bySearch; // the server order is worst-first already
    // Alt sort: soonest expiry first (nulls last), tiebreak by band then label.
    return [...bySearch].sort(
      (a, b) =>
        expiryMillis(a.expiresAt) - expiryMillis(b.expiresAt) ||
        BAND_ORDER[a.band] - BAND_ORDER[b.band] ||
        a.label.localeCompare(b.label),
    );
  }, [surface.items, category, query, sort]);

  const visible = showAll ? filtered : filtered.slice(0, PAGE_SIZE);

  // Any filter/sort/search change resets the pagination window (per the matrix).
  function selectCategory(c: CategoryFilter): void {
    setCategory(c);
    // Clear the search too: switching category with a stale query would otherwise
    // read a spurious "No items match your search" even when the category has rows.
    setSearch('');
    setShowAll(false);
  }
  function selectSort(s: SortMode): void {
    setSort(s);
    setShowAll(false);
  }
  function onSearchChange(value: string): void {
    setSearch(value);
    setShowAll(false);
  }
  function clearSearch(): void {
    setSearch('');
    setShowAll(false);
  }

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <h1 style={{ margin: 0, fontSize: 23, fontWeight: 800, letterSpacing: '-0.4px' }}>Needs attention</h1>
        <span className="muted" style={{ fontSize: 14 }}>
          <span data-numeric>{surface.total}</span> {surface.total === 1 ? 'item' : 'items'}
        </span>
      </div>

      {surface.total === 0 ? (
        <section className="card stack">
          <p className="muted" style={{ margin: 0 }}>Nothing needs attention — all clear.</p>
        </section>
      ) : (
        <section className="card stack" aria-labelledby="attention-list-heading">
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            <h2 id="attention-list-heading" style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>
              At-risk items
            </h2>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <label style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }} className="field-label">
                Sort
                <select
                  aria-label="Sort items"
                  value={sort}
                  onChange={(e) => selectSort(e.target.value as SortMode)}
                  style={{
                    padding: '6px 10px',
                    background: 'var(--surface-2)',
                    border: '1px solid var(--border)',
                    borderRadius: 'var(--rounded-md)',
                    color: 'var(--text)',
                    fontFamily: 'var(--font-sans)',
                    fontSize: 13,
                  }}
                >
                  <option value="worst">Worst status first</option>
                  <option value="expiry">Soonest expiry</option>
                </select>
              </label>
              <input
                type="search"
                aria-label="Search items by subject or framework"
                placeholder="Search…"
                value={search}
                onChange={(e) => onSearchChange(e.target.value)}
                style={{
                  padding: '6px 10px',
                  background: 'var(--surface-2)',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--rounded-md)',
                  color: 'var(--text)',
                  fontFamily: 'var(--font-sans)',
                  fontSize: 14,
                }}
              />
              {query !== '' && (
                <button type="button" className="icon-button" onClick={clearSearch}>
                  Clear
                </button>
              )}
            </div>
          </div>

          {/* Count-badged category chips. */}
          <div role="group" aria-label="Filter by category" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {CATEGORY_ORDER.map((key) => {
              const isActive = category === key;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => selectCategory(key)}
                  aria-pressed={isActive}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '5px 12px',
                    borderRadius: 'var(--rounded-full)',
                    border: `1px solid ${isActive ? 'var(--accent)' : 'var(--border)'}`,
                    background: isActive ? 'var(--accent-soft)' : 'transparent',
                    color: isActive ? 'var(--accent-on-soft)' : 'var(--text-2)',
                    fontFamily: 'var(--font-sans)',
                    fontWeight: 700,
                    fontSize: 13,
                    cursor: 'pointer',
                  }}
                >
                  {CATEGORY_LABEL[key]}
                  <span data-numeric style={{ opacity: 0.8 }}>
                    {chipCount[key]}
                  </span>
                </button>
              );
            })}
          </div>

          {filtered.length === 0 ? (
            <p className="muted" style={{ margin: 0 }}>
              {query !== '' ? 'No items match your search.' : 'No items in this category.'}
            </p>
          ) : (
            <>
              <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
                {visible.map((item) => (
                  <AttentionRow key={item.id} item={item} onOpenRecord={onOpenRecord} />
                ))}
              </ul>
              {filtered.length > PAGE_SIZE && (
                <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                  <span className="muted" style={{ fontSize: 13 }}>
                    Showing <span data-numeric>{visible.length}</span> of{' '}
                    <span data-numeric>{filtered.length}</span>
                  </span>
                  {!showAll && (
                    <button type="button" className="icon-button" onClick={() => setShowAll(true)}>
                      Show all
                    </button>
                  )}
                </div>
              )}
            </>
          )}
        </section>
      )}
    </>
  );
}

/**
 * One at-risk row: subject label, framework code (mono), status pill (dot + label),
 * the one-line reason caption, the expiry (mono when present), and a keyboard-
 * operable Open control drilling to the record. `aria-label` on the row's Open
 * control combines the label + status + reason so a red/amber state is never
 * unexplained to a screen reader.
 */
function AttentionRow({
  item,
  onOpenRecord,
}: {
  item: NeedsAttentionSurfaceItem;
  onOpenRecord: (target: DrillTarget) => void;
}): React.JSX.Element {
  return (
    <li
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        flexWrap: 'wrap',
        borderTop: '1px solid var(--border)',
        paddingTop: 8,
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1, minWidth: 200 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 700 }}>{item.label}</span>
          {item.frameworkCode && (
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 13 }} data-numeric>
              {item.frameworkCode}
            </span>
          )}
          <BandPill band={item.band} />
        </div>
        {/* One-line reason caption — a red/amber state is never unexplained (UX-DR13). */}
        <span className="muted" style={{ fontSize: 13 }}>{item.detail}</span>
      </div>
      {item.expiresAt && (
        <span className="muted" style={{ fontFamily: 'var(--font-mono)', fontSize: 13 }} data-numeric>
          {shortDate(item.expiresAt)}
        </span>
      )}
      <button
        type="button"
        className="icon-button"
        onClick={() => onOpenRecord({ kind: item.subjectType, id: item.subjectId })}
        aria-label={`Open ${item.label} — ${BAND_LABEL[item.band]}: ${item.detail}`}
      >
        Open
      </button>
    </li>
  );
}
